import React, { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { CardPageHeader } from "ctech-react-components";
import { Typography } from "@mui/material";

import { Add as AddIcon } from "@mui/icons-material";
import manWomanBoxes from "assets/supergasbras/images/manWomanBoxes.svg";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { handleGetDealerUsersRequest } from "store/modules/DealerUsers/thunk.store";
import { useAuth } from "context/AuthContext";
import {
  DealerUserRecoveryPasswordRequest,
  DealerUsersProps,
} from "store/modules/DealerUsers/types.store";
import * as Styled from "./styles";
import TableListUsers from "./components/TableListUsers";
import ModalAddUser from "./components/ModalAddUser";
import ModalEditUser from "./components/ModalEditUser";
import ModalTemporaryPassword from "./components/ModalTemporaryPassword";
import authApi from "services/authApi";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import ModalConfirmDeleteUser from "./components/ModalConfirmDeleteUser";

const ManageUsers = () => {
  const { dealerId } = useAuth();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data, pagination } = useAppSelector((state) => state?.dealerUsers);
  const [isAddNewUserModalVisible, setAddNewUserModalVisible] = useState(false);
  const [isEditUserModalVisible, setEditUserModalVisible] = useState(false);
  const [userDeleteModalVisible, setUserDeleteModalVisible] = useState(false);
  const [
    isSendedTemporaryPasswordModalVisible,
    setSendedTemporaryPasswordModalVisible,
  ] = useState(false);
  const [user, setUser] = useState<DealerUsersProps | null>(null);
  const [userEmailOrCPF, setUserEmailOrCPF] = useState<string | null>(null);
  const [typeProfile, setTypeProfile] = useState<string | undefined>(undefined);

  const token = localStorage.getItem("@ctech:token");

  const handleAddNewAreaModalVisibility = () => {
    setAddNewUserModalVisible(!isAddNewUserModalVisible);
  };

  const handleEditUserModalVisibility = (dataUser: DealerUsersProps) => {
    setUser(dataUser);
    setEditUserModalVisible(!isEditUserModalVisible);
  };

  const handleSendedTemporaryPasswordModalVisibility = async (
    dataUser: DealerUserRecoveryPasswordRequest,
  ) => {
    try {
      dispatch(setIsSpinnerVisible(true));
      const DataBody = dataUser.email
        ? {
            email: dataUser.email,
            scope: "Dealer",
          }
        : {
            cpf: dataUser.cpf,
            scope: "Dealer",
          };

      setTypeProfile(dataUser.scope);

      const { data: dataResponse } = await authApi.put(
        `/v1/users/password/temporary`,

        DataBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setUserEmailOrCPF(dataUser.cpf ? dataResponse : dataUser.email);

      setSendedTemporaryPasswordModalVisible(
        !isSendedTemporaryPasswordModalVisible,
      );
      dispatch(setIsSpinnerVisible(false));
    } catch (error) {
      dispatch(setIsSpinnerVisible(false));
    }
  };

  const [page, setPage] = useState({
    limit: 20,
    offset: 0,
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage({ ...page, offset: newPage || 1 - 1 });
  };

  const handleRowsPerPageChange = (rows: number) => {
    setPage({ ...page, limit: rows });
  };

  const searchUsers = useCallback(async () => {
    await dispatch(
      handleGetDealerUsersRequest({
        dealerId,
        ...page,
      }),
    ).unwrap();
  }, [dispatch, page, dealerId]);

  const handleDeleteUser = useCallback(async () => {
    setUserDeleteModalVisible(!userDeleteModalVisible);
  }, [userDeleteModalVisible]);

  useEffect(() => {
    searchUsers();
  }, [dispatch, page, searchUsers]);

  return (
    <Styled.Container>
      <Styled.Content>
        <ModalAddUser
          isOpen={isAddNewUserModalVisible}
          onClose={handleAddNewAreaModalVisibility}
          onCloseSuccess={() => {
            dispatch(
              handleGetDealerUsersRequest({
                dealerId,
                ...page,
              }),
            );
          }}
        />
        <ModalEditUser
          user={user}
          isOpen={isEditUserModalVisible}
          onClose={() => {
            setEditUserModalVisible(!isEditUserModalVisible);
          }}
          passwordTemporary={handleSendedTemporaryPasswordModalVisibility}
          deleteUser={handleDeleteUser}
        />
        <ModalConfirmDeleteUser
          user={user}
          isOpen={userDeleteModalVisible}
          onClose={() => {
            setUserDeleteModalVisible(!userDeleteModalVisible);
          }}
        />
        <ModalTemporaryPassword
          typeProfile={typeProfile}
          email={userEmailOrCPF}
          isOpen={isSendedTemporaryPasswordModalVisible}
          onClose={() => {
            setSendedTemporaryPasswordModalVisible(
              !isSendedTemporaryPasswordModalVisible,
            );
          }}
        />
        <CardPageHeader
          urlImage={manWomanBoxes}
          labelButton={t("addUser")}
          actionButton={handleAddNewAreaModalVisibility}
          title={<Typography variant="h5">{t("allDealerUsers")}</Typography>}
          buttonEndIcon={<AddIcon />}
        />

        {data?.length ? (
          <TableListUsers
            data={data}
            rowsPerPageOptions={[20]}
            rowsPerPage={page?.limit}
            page={page?.offset}
            totalCount={pagination?.totalCount ?? 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={({ target }) =>
              handleRowsPerPageChange(Number(target.value))
            }
            itemActions={{
              edit: handleEditUserModalVisibility,
              passwordTemporary: handleSendedTemporaryPasswordModalVisibility,
            }}
          />
        ) : (
          ""
        )}
      </Styled.Content>
    </Styled.Container>
  );
};

export default ManageUsers;
